import {Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {Ofwca} from "../../../shared/interface/ofwca";
import {Contract} from "../../../shared/interface/contract";
import {
  FormArray, FormBuilder, FormControl, FormGroup, Validators, ValidationErrors, ValidatorFn, AbstractControl
} from "@angular/forms";
import {Registration} from "../../../shared/interface/registration";
import {ReplaySubject} from "rxjs";
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {FormHelperService} from "../../../shared/services/form-helper.service";
import {OfwcaService} from "../../../shared/services/ofwca.service";
import {MatSnackBar} from "@angular/material/snack-bar";
import {RegistrationService} from "../../../shared/services/registration.service";
import {ListResponse} from "../../../shared/interface/response/list-response";
import {HttpErrorResponse} from "@angular/common/http";
import {takeUntil} from "rxjs/operators";
import {REGISTRATION_STATUS_REGISTERED} from "../../../shared/services/dict.service";

export interface ChangeRegistrationDialogData {
  registrationObject: Registration;
  ofwcaObject: Ofwca;
  text: string;
}

interface NewRegistrationForm {
  currentRegistration: Registration;
  newRegistrationControl: FormControl;
}

@Component({
  selector: 'app-cancel-registration-dialog',
  templateUrl: './cancel-registration-dialog.component.html',
  styleUrls: ['./cancel-registration-dialog.component.scss']
})
export class CancelRegistrationDialogComponent implements OnInit, OnDestroy {

  registrationObject: Registration;
  ofwcaObject: Ofwca;
  cancelRegistrationForm: FormGroup;
  reasonControl: FormControl;
  cessionControl: FormControl;
  newRegistrationsArray: FormArray;
  formErrors = {};
  registrations: Registration[] = [];
  cancelRegistrationForms: NewRegistrationForm[] = [];
  processingRequest = false;
  ofwcaRegistrations: ReplaySubject<Registration[]> = new ReplaySubject<Registration[]>(1);
  cancelRegistrationFormValid = false;
  text = '';

  readonly REGISTRATION_STATUS_REGISTERED = REGISTRATION_STATUS_REGISTERED;


  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    public dialogRef: MatDialogRef<CancelRegistrationDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ChangeRegistrationDialogData,
    private fb: FormBuilder,
    private formHelper: FormHelperService,
    private snack: MatSnackBar,
    private registrationService: RegistrationService,
    private ofwcaService: OfwcaService,
  ) {
    this.registrationObject = data.registrationObject;
    this.ofwcaObject = data.ofwcaObject;
    this.text = data.text;
    this.cancelRegistrationForm = fb.group({
      reason: this.reasonControl = fb.control('', [Validators.required]),
      newRegistration: this.newRegistrationsArray = fb.array([]),
      cession: this.cessionControl = fb.control(false),
      registration: fb.control(this.registrationObject?.uuid),
      ofwca: fb.control(this.ofwcaObject?.uuid),
    });
    if (this.ofwcaObject != null) {
      this.loadOfwcaRegistration();
    } else if (this.registrationObject.insuranceCompany.cession){
      const newRegistrationControl = this.fb.control('', []);

      this.newRegistrationsArray.push(this.fb.group({
        currentRegistration: this.fb.control(this.registrationObject.uuid),
        newRegistration: newRegistrationControl,
      }));
      this.cancelRegistrationForms.push({
        currentRegistration: this.registrationObject,
        newRegistrationControl,
      });
    }
  }

  ngOnInit(): void {
    this.cancelRegistrationForm.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => this.cancelRegistrationFormValid = this.cancelRegistrationForm.valid);


    this.cessionControl.valueChanges
      .pipe(
        takeUntil(this.destroyed$)
      )
      .subscribe((val: boolean) => {
        if (val) {
          this.newRegistrationsArray.setValidators([this.atLeastOneRegistrationSelected()]);
        } else {
          this.newRegistrationsArray.clearValidators();
        }
        this.newRegistrationsArray.updateValueAndValidity();
        this.cancelRegistrationFormValid = this.cancelRegistrationForm.valid;
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  loadOfwcaRegistration(): void {
    this.ofwcaService.getOfwcaRegstration(this.ofwcaObject.uuid)
      .subscribe((response: ListResponse<Registration>) => {
        response.items.forEach((registration: Registration) => {
          const newRegistrationControl = this.fb.control('');
          this.newRegistrationsArray.push(this.fb.group({
            currentRegistration: this.fb.control(registration.uuid),
            newRegistration: newRegistrationControl,
          }));

          this.cancelRegistrationForms.push({
            currentRegistration: registration,
            newRegistrationControl,
          });

          this.registrations.push(registration);
          this.ofwcaRegistrations.next(this.registrations);
        });
      });
  }

  atLeastOneRegistrationSelected(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const formArray = control as FormArray;
      const hasOneSelected = formArray.controls.some(
        (group) => group instanceof FormGroup && !!group.get('newRegistration')?.value
      );
      return hasOneSelected ? null : { atLeastOneRequired: true };
    };
  }

  sendForm(): void {
    this.processingRequest = true;
    this.formHelper.removeBackendErrorState(this.cancelRegistrationForm, this.formErrors);
    this.formErrors = {};

    this.registrationService.cancelRegistration(this.cancelRegistrationForm.getRawValue())
      .subscribe(() => {
        this.snack.open('Pomyślnie zaktualizowano rejestrację', '', {
          duration: 2000,
        });
        this.processingRequest = false;
        this.dialogRef.close();
      }, (error: HttpErrorResponse) => {
        this.processingRequest = false;

        if (error.error.error.text){
          this.snack.open(error.error.error.text, '400', {duration: 4000});
        }else {
          this.snack.open('Podczas aktualizacji obiektu rejestrację wystąpił błąd', error.status.toString(), {
            duration: 2000,
          });
        }

        if (error.status === 400) {
          this.formErrors = error.error.error.list;
          this.formHelper.setBackendErrorState(this.cancelRegistrationForm, this.formErrors);
        }
      })
    ;
  }

}
